import { IntlShape } from "react-intl";
import { FieldValues, UseFormClearErrors, UseFormGetValues, UseFormSetError } from "react-hook-form";

import { getNowInPreferredTimezone, parsePreferredWallClock } from "helpers/dateTimeHelper";
import { isDeadlineDateInPast } from "@RequestFormV2/validation/utils/isDeadlineDateInPast";
import { isEmptyValue, isValidTimeValue } from "@RequestFormV2/validation/utils/validationMessages";

export const QUOTING_DEADLINE_MINIMUM_ERROR_PATH = "shipment.quoting_deadline_minimum";

function toWallClockTime(time: unknown): string | null {
  if (!isValidTimeValue(time)) return null;
  const digits = String(time).replace(/\D/g, "").slice(0, 4);
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:00`;
}

/**
 * True when the quoting deadline is earlier than the company minimum lead time.
 * A deadline exactly `minimumMinutes` ahead is allowed ("at least").
 */
export function isQuotingDeadlineBeforeMinimum(
  date: unknown,
  time: unknown,
  minimumMinutes: number,
  explicitTz?: string | null,
): boolean {
  if (typeof date !== "string" || !date.trim()) return false;
  const wallClock = toWallClockTime(time);
  if (!wallClock) return false;

  const deadline = parsePreferredWallClock(date, wallClock, explicitTz);
  if (!deadline) return false;

  return deadline.diff(getNowInPreferredTimezone(explicitTz), "minutes") < minimumMinutes;
}

export function getQuotingDeadlineMinimumMessage(intl: IntlShape, minimumMinutes: number): string {
  return intl.formatMessage(
    {
      id: "request-form.validation.quotingDeadlineMinimum",
      defaultMessage: "Deadline must be at least {minutes} minutes ahead from now",
    },
    { minutes: minimumMinutes },
  );
}

export function validateQuotingDeadlineMinimum(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
  minimumMinutes: number | null | undefined,
  explicitTz?: string | null,
  clearErrors?: UseFormClearErrors<FieldValues>,
): boolean {
  if (minimumMinutes == null || minimumMinutes < 0) {
    clearErrors?.(QUOTING_DEADLINE_MINIMUM_ERROR_PATH);
    return true;
  }

  const date = getValues("shipment.quoting_deadline_date");
  const time = getValues("shipment.quoting_deadline_time");
  if (isEmptyValue(date) || !isValidTimeValue(time) || isDeadlineDateInPast(date, explicitTz)) {
    clearErrors?.(QUOTING_DEADLINE_MINIMUM_ERROR_PATH);
    return true;
  }

  if (isQuotingDeadlineBeforeMinimum(date, time, minimumMinutes, explicitTz)) {
    setError(QUOTING_DEADLINE_MINIMUM_ERROR_PATH, {
      type: "manual",
      message: getQuotingDeadlineMinimumMessage(intl, minimumMinutes),
    });
    return false;
  }

  clearErrors?.(QUOTING_DEADLINE_MINIMUM_ERROR_PATH);
  return true;
}
